import { Buffer } from "buffer";
import fontkit from "fontkit";
import once from "lodash-es/once";

const getPrintableASCIICharacters = once(() => {
  const characters: string[] = [];
  for (let i = 32; i <= 127; i += 1) {
    characters.push(String.fromCharCode(i));
  }
  return characters;
});

const defaultFontCache: Map<string, Map<string, number>> = new Map();

const getFontCacheKey = (family: string, variant: string) =>
  `${family}---${variant}`;

// Create a fallback font object with safe methods
const createFallbackFont = () => {
  return {
    unitsPerEm: 1000,
    glyphForCodePoint: (codePoint: number) => ({
      advanceWidth: 500, // Default width for fallback
    }),
    // Add other necessary methods and properties
    getGlyph: () => ({
      advanceWidth: 500,
    }),
    e: () => true, // Add the e method that's causing issues
  };
};

const getFontObject = async (): Promise<any> => {
  try {
    const fontFileResponse = await fetch(
      require("../..//fonts/source-sans-pro-v13-latin-regular.ttf"),
      { credentials: "include" }
    );
    if (fontFileResponse.ok) {
      const arrayBuffer = await fontFileResponse.arrayBuffer();
      const nodeJSBuffer = new Buffer(arrayBuffer);
      try {
        const font = fontkit.create(nodeJSBuffer);
        return font;
      } catch (error) {
        console.warn("Error creating font:", error);
        return createFallbackFont();
      }
    } else {
      console.warn("Could not fetch font file");
      return createFallbackFont();
    }
  } catch (error) {
    console.warn("Error in getFontObject:", error);
    return createFallbackFont();
  }
};

export const getCharacterWidthMap = async (
  fontCache = defaultFontCache
): Promise<Map<string, number>> => {
  try {
    const cacheKey = getFontCacheKey("", "");
    const cachedResult = fontCache.get(cacheKey);
    if (cachedResult === undefined) {
      const result: Map<string, number> = new Map();
      const fontObject = await getFontObject();
      const unitsPerEm = fontObject.unitsPerEm || 1000; // Provide fallback value
      const characters = getPrintableASCIICharacters();

      for (const character of characters) {
        try {
          const codePoint = character.codePointAt(0);
          if (codePoint === undefined) {
            result.set(character, 0.5); // Default width if codePoint is undefined
            continue;
          }

          let glyph;
          try {
            glyph = fontObject.glyphForCodePoint(codePoint);
          } catch (error) {
            console.warn(
              `Error getting glyph for code point ${codePoint}:`,
              error
            );
            result.set(character, 0.5); // Default width if glyph retrieval fails
            continue;
          }

          if (!glyph || typeof glyph.advanceWidth !== "number") {
            result.set(character, 0.5); // Default width if glyph or advanceWidth is invalid
            continue;
          }

          const width = glyph.advanceWidth / unitsPerEm;
          result.set(character, width);
        } catch (error) {
          console.warn(`Error processing character '${character}':`, error);
          result.set(character, 0.5); // Default width on error
        }
      }

      fontCache.set(cacheKey, result);
      return result;
    } else {
      return cachedResult;
    }
  } catch (error) {
    console.warn("Error in getCharacterWidthMap:", error);
    // Return a fallback map with default values
    const fallbackMap = new Map<string, number>();
    const characters = getPrintableASCIICharacters();
    for (const character of characters) {
      fallbackMap.set(character, 0.5); // Default width
    }
    return fallbackMap;
  }
};
